(() => {
  if (customElements.get('godness-waitlist')) return;

  const STORAGE_KEY = 'godness:waitlist:v1';
  const DAY = 24 * 60 * 60 * 1000;

  class GodnessWaitlist extends HTMLElement {
    constructor() {
      super();
      this.onOpen = this.onOpen.bind(this);
      this.close = this.close.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onAutoContextChange = this.onAutoContextChange.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-waitlist-dialog]');
      this.inline = this.querySelector('[data-waitlist-inline]');
      this.content = this.querySelector('[data-waitlist-content]');
      this.dialogSlot = this.querySelector('[data-waitlist-slot]');
      this.trigger = this.querySelector('[data-waitlist-trigger]');
      this.closer = this.querySelector('[data-waitlist-close]');
      this.stores = this.availableStores();
      if (this.dataset.knownSubscriber === 'true' || this.content?.querySelector('[data-waitlist-success]')) {
        this.saveState({ joined: true });
      }
      if (!this.dialog?.showModal || !this.inline || !this.content || !this.dialogSlot) return;

      this.dialogSlot.append(this.content);
      this.inline.hidden = true;
      if (this.trigger) this.trigger.hidden = false;
      this.openers = Array.from(document.querySelectorAll('[data-waitlist-open]'))
        .filter(opener => this.matchesOpener(opener));
      this.openers.forEach(opener => {
        opener.setAttribute('aria-haspopup', 'dialog');
        opener.setAttribute('aria-controls', this.dialog.id);
      });
      document.addEventListener('click', this.onOpen);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.closer?.addEventListener('click', this.close);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('click', this.onBackdropClick);

      // Native Shopify results must stay visible, even after a recent dismissal.
      if (this.content.querySelector('[data-waitlist-result]')) this.open(this.trigger);
      else this.scheduleAutoOpen();
    }

    disconnectedCallback() {
      this.stopAutoOpen();
      this.openers?.forEach(opener => {
        opener.removeAttribute('aria-haspopup');
        opener.removeAttribute('aria-controls');
      });
      document.removeEventListener('click', this.onOpen);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.closer?.removeEventListener('click', this.close);
      this.dialog?.removeEventListener('close', this.onClose);
      this.dialog?.removeEventListener('click', this.onBackdropClick);
      this.close();
      this.restoreOverflow();
      if (this.inline && this.content) {
        this.inline.append(this.content);
        this.inline.hidden = false;
      }
      if (this.trigger) this.trigger.hidden = true;
    }

    availableStores() {
      return ['localStorage', 'sessionStorage'].flatMap(name => {
        try {
          const storage = window[name];
          const probe = `${STORAGE_KEY}:probe`;
          storage.setItem(probe, '1');
          storage.removeItem(probe);
          return [storage];
        } catch (_) {
          return [];
        }
      });
    }

    readState() {
      const state = { shownAt: 0, joined: this.dataset.knownSubscriber === 'true' };
      this.stores = (this.stores || []).filter(storage => {
        try {
          const stored = JSON.parse(storage.getItem(STORAGE_KEY) || '{}');
          if (stored && typeof stored === 'object') {
            if (Number.isFinite(stored.shownAt)) state.shownAt = Math.max(state.shownAt, stored.shownAt);
            state.joined = state.joined || stored.joined === true;
          }
          return true;
        } catch (_) {
          // Corrupt data is replaceable; an inaccessible store cannot safely throttle.
          try { storage.getItem(STORAGE_KEY); return true; } catch (_) { return false; }
        }
      });
      return state;
    }

    saveState(update) {
      const state = { ...this.readState(), ...update };
      this.stores = this.stores.filter(storage => {
        try {
          storage.setItem(STORAGE_KEY, JSON.stringify(state));
          return true;
        } catch (_) {
          return false;
        }
      });
    }

    canAutoOpen() {
      const state = this.readState();
      const days = Number(this.dataset.dismissDays);
      const cooldown = (Number.isFinite(days) && days > 0 ? days : 7) * DAY;
      return this.dataset.autoEnabled === 'true' && this.stores.length > 0 &&
        !state.joined && (!state.shownAt || Date.now() - state.shownAt >= cooldown);
    }

    clearAutoTimer() {
      window.clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }

    isVisibleForAutoOpen(element) {
      return Boolean(element && !element.hidden &&
        !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
        element.getClientRects().length && window.getComputedStyle(element).visibility === 'visible');
    }

    hasAutoOpenBlocker() {
      const editing = document.activeElement?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
      if (this.isVisibleForAutoOpen(editing)) return true;
      // The non-modal cookie banner may remain after preferences close.
      return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], [role="dialog"]:not([aria-modal="false"]), [role="alertdialog"]:not([aria-modal="false"])'))
        .some(modal => modal !== this.dialog && this.isVisibleForAutoOpen(modal));
    }

    startAutoWatch() {
      if (this.autoObserver) return;
      // Cookie preferences can close by saving, Escape, or removing their DOM.
      // Observe all of those paths without changing the visitor's consent.
      this.autoObserver = new MutationObserver(this.onAutoContextChange);
      this.autoObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'hidden', 'inert', 'aria-hidden', 'aria-modal', 'role', 'class', 'style']
      });
      document.addEventListener('focusin', this.onAutoContextChange);
      document.addEventListener('focusout', this.onAutoContextChange);
    }

    pauseAutoTimer() {
      if (this.autoTimer != null) {
        this.autoRemaining = Math.max(0, this.autoRemaining - (Date.now() - this.autoStartedAt));
      }
      this.clearAutoTimer();
      this.autoStartedAt = null;
    }

    stopAutoOpen() {
      this.clearAutoTimer();
      this.autoStartedAt = null;
      this.autoObserver?.disconnect();
      this.autoObserver = null;
      document.removeEventListener('focusin', this.onAutoContextChange);
      document.removeEventListener('focusout', this.onAutoContextChange);
    }

    scheduleAutoOpen() {
      if (!this.isConnected || this.autoHandled || !this.canAutoOpen()) {
        this.stopAutoOpen();
        return;
      }
      if (this.autoRemaining == null) {
        const delay = Number(this.dataset.autoDelay);
        this.autoRemaining = Number.isFinite(delay) && delay >= 0 ? delay : 5000;
      }
      this.startAutoWatch();
      if (document.hidden || this.hasAutoOpenBlocker()) {
        this.pauseAutoTimer();
        return;
      }
      if (this.autoTimer != null) return;
      this.autoStartedAt = Date.now();
      this.autoTimer = window.setTimeout(() => {
        this.autoTimer = null;
        this.autoStartedAt = null;
        this.autoRemaining = 0;
        if (!this.isConnected || this.autoHandled || !this.canAutoOpen()) {
          this.stopAutoOpen();
          return;
        }
        // A blocker appearing at the deadline must leave the offer pending.
        if (document.hidden || this.hasAutoOpenBlocker()) return;
        this.open(document.activeElement);
      }, this.autoRemaining);
    }

    onAutoContextChange() {
      this.scheduleAutoOpen();
    }

    onVisibilityChange() {
      this.scheduleAutoOpen();
    }

    matchesOpener(opener) {
      return Boolean(opener && this.dialog && (opener.dataset.waitlistOpen === this.dialog.id ||
        (this.dataset.waitlistKey && opener.dataset.waitlistOpen === this.dataset.waitlistKey)));
    }

    onOpen(event) {
      const opener = event.target.closest?.('[data-waitlist-open]');
      if (!this.matchesOpener(opener) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.setAttribute('aria-controls', this.dialog.id);
      this.open(opener);
    }

    open(opener) {
      if (!this.isConnected || this.dialog.open) return;
      this.opener = opener;
      this.dialog.showModal();
      this.autoHandled = true;
      this.stopAutoOpen();
      this.saveState({ shownAt: Date.now() });
      this.savedOverflow = [document.documentElement, document.body].map(element => ({
        element,
        value: element.style.getPropertyValue('overflow'),
        priority: element.style.getPropertyPriority('overflow')
      }));
      this.savedOverflow.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
      const result = this.content.querySelector('[data-waitlist-result]');
      // Focusing text avoids raising the on-screen keyboard before the visitor opts in.
      (result || this.content.querySelector('.redeemed-waitlist__title') || this.closer)?.focus({ preventScroll: true });
    }

    close() {
      this.stopAutoOpen();
      this.autoHandled = true;
      if (this.dialog?.open) this.dialog.close();
    }

    restoreOverflow() {
      this.savedOverflow?.forEach(({ element, value, priority }) => {
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      this.savedOverflow = null;
    }

    onClose() {
      this.stopAutoOpen();
      this.autoHandled = true;
      this.saveState({ shownAt: Date.now() });
      this.restoreOverflow();
      if (this.isConnected && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    onBackdropClick(event) {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    }
  }

  customElements.define('godness-waitlist', GodnessWaitlist);
})();
